import React, { useState, useRef, useEffect } from 'react';

const IntentProgressBox = ({ intent, messages, allMessages = [], isCurrent, isCompleted, isFailed, position, errorMessage, onIntentClick }) => {
  const [showJson, setShowJson] = useState(false);
  const [showCartState, setShowCartState] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // Debug log
  console.log(`Intent ${intent.id} validation_score:`, intent.validation_score);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  
  const getIntentTypeLabel = () => {
    switch (intent.intent_type) {
      case 'message': return 'Message';
      case 'inquiry': return 'Inquiry';
      case 'cart_add': return 'Add to Cart';
      case 'cart_combo_add': return 'Add Combo';
      case 'cart_modify': return 'Modify Cart';
      case 'cart_remove': return 'Remove from Cart';
      case 'cart_clear': return 'Clear Cart';
      default: return intent.intent_type;
    }
  };
  
  const getIntentTypeDescription = () => {
    switch (intent.intent_type) {
      case 'message': 
        return 'Sends a simple message to the bot without expecting any specific action';
      case 'inquiry': 
        return 'Asks the bot to perform a specific task or answer a question, validated by AI';
      case 'cart_add':
        return 'Adds items to the shopping cart with specific options and addons';
      case 'cart_combo_add':
        return 'Adds combo deals to the shopping cart with subgroup selections';
      case 'cart_modify': 
        return 'Changes existing items in the cart (e.g., quantity, options, or addons)';
      case 'cart_remove': 
        return 'Removes specific items from the shopping cart';
      case 'cart_clear': 
        return 'Empties the entire shopping cart';
      default: 
        return 'Unknown intent type';
    }
  };
  
  const getExplicitnessDescription = (level) => {
    switch(level) {
      case 'minimal':
        return 'Customer mentions only item name initially, details come later';
      case 'progressive':
        return 'Customer provides item and size first, then addons in follow-up';
      case 'full':
        return 'Customer provides all details in one complete message';
      default:
        return level;
    }
  };
  
  const renderIntentContent = () => {
    switch (intent.intent_type) {
      case 'message':
        return renderMessageIntentContent();
      case 'inquiry':
        return renderInquiryIntentContent();
      case 'cart_add':
      case 'cart_combo_add':
        return renderCartAddIntentContent();
      case 'cart_modify':
      case 'cart_remove':
      case 'cart_clear':
        return renderCartActionIntentContent();
      default:
        return <div className="text-sm text-gray-500">Unknown intent type</div>;
    }
  };
  
  const renderCartActionIntentContent = () => {
    const content = intent.intent_spec?.content || 'No action specified';
    const validation = intent.intent_spec?.validation || {};
    const maxTurns = intent.max_turns || 2;
    
    return (
      <>
        <div className="text-sm text-gray-300 mt-2 bg-slate-700 p-2 rounded border border-slate-600">
          {content}
        </div>
        
        
        {validation.rubric && (
          <div className="text-xs text-gray-400 mt-1 italic bg-slate-700 p-2 rounded">
            <span className="text-cyan-400 font-medium">Expected:</span> {validation.rubric}
          </div>
        )}
        
      </>
    );
  };
  
  // Get the latest bot message's cart state for cart intents
  const isCartIntent = intent.intent_type.startsWith('cart_');
  const latestBotMessage = isCartIntent ? 
    [...messages].reverse().find(m => m.direction === 'bot') : null;
  const cartState = latestBotMessage?.cart_state;
  
  const getCardClass = () => {
    if (isFailed) return 'border-red-500/20 bg-slate-800 shadow-lg shadow-red-500/10';
    if (isCurrent) return 'border-cyan-500/20 bg-slate-800 shadow-lg shadow-cyan-500/10';
    if (isCompleted) return 'border-emerald-500/20 bg-slate-800 shadow-md';
    return 'border-gray-600 bg-slate-800 shadow-sm';
  };

  const getHeaderClass = () => {
    // Message intents don't have validation and are always successful
    const isMessageIntent = intent.intent_type === 'message';
    
    // Check if validation failed (score below pass threshold)
    const validationFailed = !isMessageIntent && 
      intent.validation_score !== undefined && 
      intent.validation_score < (intent.validation?.pass_score || 0.7);
    
    if (isFailed || (isCompleted && validationFailed)) return 'bg-gradient-to-r from-red-600 to-red-700';
    if (isCurrent) return 'bg-gradient-to-r from-cyan-600 to-blue-600';
    if (isCompleted) return 'bg-gradient-to-r from-emerald-600 to-emerald-700';
    return 'bg-gradient-to-r from-gray-600 to-gray-700';
  };

  const getStatusIcon = () => {
    if (isFailed) {
      return (
        <span className="text-white">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </span>
      );
    }
    if (isCompleted) {
      return (
        <span className="text-white">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </span>
      );
    }
    if (isCurrent) {
      return (
        <span className="text-white">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </span>
      );
    }
    return (
      <span className="text-gray-400">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </span>
    );
  };

  const renderMessageIntentContent = () => {
    const content = intent.intent_spec?.content || 'No message specified';
    
    return (
      <>
        <div className="text-sm text-gray-300 mt-2 bg-slate-700 p-2 rounded border border-slate-600">
          {content}
        </div>
      </>
    );
  };

  const renderInquiryIntentContent = () => {
    const content = intent.intent_spec?.content || 'No instruction specified';
    const validation = intent.intent_spec?.validation || {};
    const maxTurns = intent.max_turns || 3;
    
    return (
      <>
        <div className="text-sm text-gray-300 mt-2 bg-slate-700 p-2 rounded border border-slate-600">
          {content}
        </div>
        
        {validation.rubric && (
          <div className="text-xs text-gray-400 mt-1 italic bg-slate-700 p-2 rounded">
            <span className="text-cyan-400 font-medium">Expected:</span> {validation.rubric}
          </div>
        )}
        
        <div className="mt-3 space-y-1">
          {validation.pass_score && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-400">Pass score:</span>
              <span className="font-medium text-gray-300">{validation.pass_score}</span>
            </div>
          )}
          {intent.validation_score !== undefined && intent.validation_score !== null && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-400">Actual score:</span>
              <span className={`font-medium ${
                intent.validation_score >= validation.pass_score ? 'text-green-400' : 'text-red-400'
              }`}>
                {intent.validation_score.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderCartAddIntentContent = () => {
    const expectedItems = intent.intent_spec?.content?.items || [];
    const expectedCombos = intent.intent_spec?.content?.combos || [];
    
    // Get the initial cart state (before this intent started)
    // Find the index of the first message in this intent within allMessages
    const firstMessageId = messages.length > 0 ? messages[0].id : null;
    const firstMessageIndex = firstMessageId ? allMessages.findIndex(m => m.id === firstMessageId) : -1;
    
    // Get the last bot message before this intent started
    const previousBotMessage = firstMessageIndex > 0 ? 
      allMessages.slice(0, firstMessageIndex).reverse().find(m => m.direction === 'bot' && m.cart_state) : null;
    const initialCartItems = previousBotMessage?.cart_state?.items || [];
    const initialCartCombos = previousBotMessage?.cart_state?.combos || [];
    
    // Get the final cart state (after this intent)
    const finalCartItems = cartState ? cartState.items || [] : [];
    const finalCartCombos = cartState ? cartState.combos || [] : [];
    
    const getAddonName = (addon) => {
      if (typeof addon === 'string') return addon;
      if (addon && typeof addon === 'object') return addon.name || JSON.stringify(addon);
      return String(addon);
    };

    const getAddonQuantity = (addon) => {
      if (!addon || typeof addon !== 'object') return 1;

      const parsedQty = Number(addon.quantity);
      if (Number.isFinite(parsedQty) && parsedQty > 0) return parsedQty;

      return 1;
    };

    const aggregateAddons = (addons) => {
      if (!Array.isArray(addons) || addons.length === 0) return [];

      const addonMap = new Map();

      addons.forEach((addon) => {
        const name = getAddonName(addon);
        const quantity = getAddonQuantity(addon);
        const key = name.toLowerCase().trim();
        const existing = addonMap.get(key);

        if (existing) {
          existing.quantity += quantity;
        } else {
          addonMap.set(key, { name, quantity });
        }
      });

      return Array.from(addonMap.values());
    };
    
    // Use the validation_score from the intent if available
    const validationPassed = intent.validation_score !== undefined ? intent.validation_score >= 1.0 : (isCompleted && !isFailed);

    const renderItemDetails = (item) => {
      const parts = [];
      if (item.quantity) parts.push(`${item.quantity}x`);
      parts.push(item.name);
      if (item.option) parts.push(`(${item.option})`);
      
      return (
        <>
          <div className="font-medium text-gray-200">{parts.join(' ')}</div>
          {item.addons && item.addons.length > 0 && (
            <div className="ml-4 text-gray-400 mt-0.5">
              {aggregateAddons(item.addons).map((addon, idx) => {
                const addonQty = addon.quantity > 1 ? ` x${addon.quantity}` : '';
                return <div key={idx}>+ {addon.name}{addonQty}</div>;
              })}
            </div>
          )}
        </>
      );
    };
    
    const renderComboDetails = (combo) => {
      const parts = [];
      if (combo.quantity) parts.push(`${combo.quantity}x`);
      parts.push(combo.name);
      parts.push('Combo');
      
      return (
        <>
          <div className="font-medium text-purple-300">{parts.join(' ')}</div>
          {combo.items && combo.items.length > 0 && (
            <div className="ml-4 text-gray-400 mt-1 space-y-0.5">
              {combo.items.map((item, idx) => (
                <div key={idx} className="text-xs">
                  <span className="text-purple-400">• </span>
                  <span>{item.name}</span>
                  {item.option && <span className="text-purple-300"> ({item.option})</span>}
                  {item.addons && item.addons.length > 0 && (
                    <span className="text-gray-500"> +{aggregateAddons(item.addons).map(addon => {
                      const qty = addon.quantity > 1 ? ` x${addon.quantity}` : '';
                      return `${addon.name}${qty}`;
                    }).join(', ')}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      );
    };

    return (
      <>
        <div className="mt-3 space-y-3">
          <div className="bg-slate-700 p-3 rounded-lg border border-slate-600">
            <div className="grid grid-cols-2 gap-4">
              {/* Expected column */}
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Expected</span>
                </div>
                {expectedItems.length > 0 || expectedCombos.length > 0 ? (
                  <div className="space-y-2">
                    {expectedItems.length > 0 && (
                      <ul className="text-xs space-y-1">
                        {expectedItems.map((item, index) => (
                          <li key={index} className="bg-slate-600 px-2 py-1 rounded border border-slate-500">
                            {renderItemDetails(item)}
                          </li>
                        ))}
                      </ul>
                    )}
                    {expectedCombos.length > 0 && (
                      <ul className="text-xs space-y-1">
                        {expectedCombos.map((combo, index) => (
                          <li key={index} className="bg-purple-900/30 px-2 py-1 rounded border border-purple-700">
                            {renderComboDetails(combo)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 italic">Empty cart</div>
                )}
              </div>
              
              {/* Actual column */}
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Actual</span>
                  {validationPassed && <span className="text-xs text-emerald-400">✓</span>}
                  {!validationPassed && isCompleted && <span className="text-xs text-red-400">✗</span>}
                </div>
                {messages.length > 0 ? (
                  finalCartItems.length > 0 || finalCartCombos.length > 0 ? (
                    <div className="space-y-2">
                      {finalCartItems.length > 0 && (
                        <ul className="text-xs space-y-1">
                          {finalCartItems.map((item, index) => {
                            const wasInitialItem = initialCartItems.some(initial => 
                              initial.name?.toLowerCase() === item.name?.toLowerCase() && 
                              initial.option?.toLowerCase() === item.option?.toLowerCase()
                            );
                            
                            return (
                              <li key={index} className={`px-2 py-1 rounded border ${
                                wasInitialItem ? 'bg-slate-600 border-slate-500 opacity-60' : 'bg-slate-600 border-slate-500'
                              }`}>
                                {renderItemDetails(item)}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {finalCartCombos.length > 0 && (
                        <ul className="text-xs space-y-1">
                          {finalCartCombos.map((combo, index) => {
                            const wasInitialCombo = initialCartCombos.some(initial => 
                              initial.name?.toLowerCase() === combo.name?.toLowerCase()
                            );
                            
                            return (
                              <li key={index} className={`px-2 py-1 rounded border ${
                                wasInitialCombo ? 'bg-purple-900/30 border-purple-700 opacity-60' : 'bg-purple-900/30 border-purple-700'
                              }`}>
                                {renderComboDetails(combo)}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 italic">Empty cart</div>
                  )
                ) : (
                  <div className="text-xs text-gray-500 italic">Not executed yet</div>
                )}
              </div>
            </div>
          </div>
              
              {isFailed && errorMessage && (
                <div className="mt-2 text-xs text-red-400 bg-red-900/20 p-2 rounded border border-red-700">
                  <strong>Error:</strong> {errorMessage}
                </div>
              )}
              
              {/* Display validation analysis for failed validations */}
              {!validationPassed && intent.validation_score !== undefined && intent.validation_score < 1.0 && (
                <div className="mt-2 space-y-2">
                  {/* Find the last message in this intent with validation details */}
                  {(() => {
                    const lastMessage = messages.slice().reverse().find(m => m.validation_analysis || m.validation_reasoning);
                    if (!lastMessage) return null;
                    
                    return (
                      <div className="text-xs bg-red-900/20 border border-red-700 rounded p-2 max-w-full">
                        <div className="font-semibold text-red-400 mb-2">Validation Failed</div>
                        {lastMessage.validation_analysis && (
                          <div className="mb-2">
                            <div className="text-amber-400 font-medium mb-1">Analysis:</div>
                            <div className="text-amber-300 whitespace-pre-line overflow-x-auto max-h-24 overflow-y-auto">{lastMessage.validation_analysis}</div>
                          </div>
                        )}
                        {lastMessage.validation_reasoning && (
                          <div>
                            <div className="text-red-400 font-medium mb-1">Reason:</div>
                            <div className="text-red-300 whitespace-pre-line overflow-x-auto max-h-24 overflow-y-auto">{lastMessage.validation_reasoning}</div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
        </div>
      </>
    );
  };

  const handleCardClick = () => {
    if (onIntentClick && intent.id) {
      onIntentClick(intent.id);
    }
  };

  return (
    <div 
      className={`relative rounded-lg border transition-all duration-300 overflow-hidden ${getCardClass()} ${onIntentClick ? 'cursor-pointer hover:shadow-xl' : ''}`}
      onClick={handleCardClick}
    >
      <div className={`relative px-4 py-3 ${getHeaderClass()}`}>
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
        <div className="absolute inset-x-0 bottom-0 h-px bg-black/40"></div>
        
        {/* Tech grid pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>
        
        {/* Animated scanline for current intent */}
        {isCurrent && (
          <div className="absolute inset-x-0 top-0 h-full overflow-hidden">
            <div className="h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-60 animate-pulse"></div>
          </div>
        )}
        
        {/* Status icon in top left */}
        <div className="absolute top-2 left-2">
          <div className="relative">
            <div className={`absolute inset-0 ${
              isFailed ? 'bg-red-400' : 
              isCurrent ? 'bg-cyan-400' : 
              isCompleted ? 'bg-emerald-400' : 
              'bg-gray-400'
            } blur-md opacity-50`}></div>
            <div className="relative">
              {getStatusIcon()}
            </div>
          </div>
        </div>
        
        <div className="relative flex items-center justify-between">
          <div className="flex items-center space-x-2 ml-8">
            <div>
              <div className="relative group">
                <h3 className="font-bold text-white uppercase tracking-wider text-sm cursor-help">
                  {getIntentTypeLabel()}
                </h3>
                <div className="absolute z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 -left-2 top-full mt-2">
                  <div className="relative bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 max-w-xs">
                    <div className="font-semibold mb-1">{getIntentTypeLabel()}</div>
                    <div className="text-gray-300 text-xs leading-relaxed">
                      {getIntentTypeDescription()}
                    </div>
                    <div className="absolute -top-1 left-6 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                  </div>
                </div>
              </div>
              {(intent.intent_type === 'cart_add' || intent.intent_type === 'cart_combo_add') && intent.intent_spec?.params?.explicitness && (
                <div className="relative group mt-0.5">
                  <div className="text-[10px] text-white/50 uppercase tracking-wider cursor-help">
                    {intent.intent_spec.params.explicitness} ordering
                  </div>
                  <div className="absolute z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 -left-2 top-full mt-1">
                    <div className="relative bg-gray-900 text-white text-xs rounded-lg shadow-xl p-2 max-w-xs">
                      <div className="font-semibold mb-1 capitalize">{intent.intent_spec.params.explicitness} Ordering</div>
                      <div className="text-gray-300 text-xs leading-relaxed">
                        {getExplicitnessDescription(intent.intent_spec.params.explicitness)}
                      </div>
                      <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="p-4 overflow-x-auto bg-slate-800">
        {renderIntentContent()}
        
        {showJson && (
          <div className="mt-3 p-3 bg-slate-700 border border-slate-600 rounded text-xs overflow-x-auto">
            <pre className="whitespace-pre-wrap break-words text-gray-300">
              {JSON.stringify(intent, null, 2)}
            </pre>
          </div>
        )}
        
        {showCartState && cartState && (
          <div className="mt-3 p-3 bg-purple-900/20 border border-purple-700 rounded text-xs overflow-x-auto">
            <div className="font-medium mb-1 text-purple-400">Cart State from Latest Bot Response:</div>
            <pre className="whitespace-pre-wrap break-words text-gray-300">
              {JSON.stringify(cartState, null, 2)}
            </pre>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="px-4 py-3 border-t border-slate-700 bg-slate-900 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Dropdown menu */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDropdown(!showDropdown);
              }}
              className="p-1.5 rounded bg-slate-700 hover:bg-slate-600 transition-all duration-200 border border-slate-600 hover:border-slate-500"
              title="More options"
            >
              <svg className="w-3 h-3 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
              </svg>
            </button>
            
            {showDropdown && (
              <div className="absolute left-0 bottom-full mb-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-10">
                <button
                  onClick={() => {
                    setShowJson(!showJson);
                    setShowDropdown(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors first:rounded-t-lg"
                >
                  {showJson ? 'Hide' : 'Show'} JSON Data
                </button>
                {isCartIntent && (
                  <button
                    onClick={() => {
                      setShowCartState(!showCartState);
                      setShowDropdown(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors last:rounded-b-lg"
                  >
                    {showCartState ? 'Hide' : 'Show'} Cart State
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Message count */}
          <div className="flex items-center space-x-1 text-xs text-gray-400">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <span className="font-medium">{messages.length}</span>
          </div>
          
          {/* Intent ID */}
          <div className="flex items-center space-x-1 text-xs text-gray-400">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
            </svg>
            <span className="font-medium">{position}</span>
          </div>
          
          {/* Turns indicator */}
          <div className="flex items-center space-x-1 text-xs text-gray-400">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="font-medium">
              {Math.floor(messages.length / 2)}/{intent.max_turns}
            </span>
          </div>
        </div>
        
        {/* Validation score */}
        {intent.validation_score !== undefined && intent.validation_score !== null && (
          <div className="relative group">
            <span className={`text-xs font-mono px-2 py-1 rounded border cursor-help ${
              intent.validation_score >= (intent.validation?.pass_score || 0.7) 
                ? 'bg-emerald-900/30 text-emerald-400 border-emerald-700' 
                : 'bg-red-900/30 text-red-400 border-red-700'
            }`}>
              {intent.validation_score.toFixed(2)}
            </span>
            <div className="absolute z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 right-0 bottom-full mb-2">
              <div className="relative bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 max-w-xs">
                <div className="font-semibold mb-1">Validation Score</div>
                <div className="text-gray-300 text-xs leading-relaxed">
                  {(intent.intent_type === 'cart_add' || intent.intent_type === 'cart_combo_add')
                    ? `Cart validation: ${intent.validation_score >= 1.0 ? 'Passed' : 'Failed'}`
                    : `LLM score: ${intent.validation_score.toFixed(2)} / ${intent.validation?.pass_score || '0.70'}`
                  }
                </div>
                <div className="absolute -bottom-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntentProgressBox;
